/**
 * Builds the Home dashboard "How things are going" briefing:
 *   - summary: 1–3 portfolio-level bullets
 *   - accounts: one bullet per visible customer
 *
 * Expects metrics from computeWeekComparisonRange() — business days only,
 * excluding weekends and today's partial data.
 */

const METRICS = [
  { key: 'referrals', label: 'referrals', weight: 3 },
  { key: 'vitalRounds', label: 'VitalRounds', weight: 2 },
  { key: 'logins', label: 'logins', weight: 1 },
  { key: 'messages', label: 'messages', weight: 1 },
  { key: 'studies', label: 'studies', weight: 1 },
];

function pctChange(current, previous) {
  const c = Number(current) || 0;
  const p = Number(previous) || 0;
  if (p === 0) return c === 0 ? 0 : 100;
  return ((c - p) / p) * 100;
}

function fmtPct(n) {
  const abs = Math.abs(n);
  const rounded = abs >= 10 ? Math.round(abs) : Math.round(abs * 10) / 10;
  return `${rounded}%`;
}

function fmtDelta(n) {
  const sign = n > 0 ? '+' : '';
  return `${sign}${n}`;
}

function sumMetrics(window) {
  return METRICS.reduce((t, m) => t + (Number(window?.[m.key]) || 0), 0);
}

function comparisonPhrase(weekRange) {
  if (!weekRange?.hasBusinessDays) return 'in the comparison window';
  if (weekRange.businessDayCount === 1) {
    return `vs ${weekRange.previousLabel}`;
  }
  return `vs prior ${weekRange.currentLabel}`;
}

function describeReferralTrend(curr, prev, phrase) {
  const refPct = pctChange(curr, prev);
  if (curr === 0 && prev === 0) {
    return {
      tone: 'neutral',
      text: `No referral activity yet ${phrase}.`,
    };
  }
  if (refPct > 10) {
    return {
      tone: 'good',
      text: `Referrals are up ${fmtPct(refPct)} ${phrase} (${curr} vs ${prev} across your book).`,
    };
  }
  if (refPct < -10) {
    return {
      tone: 'bad',
      text: `Referrals are down ${fmtPct(refPct)} ${phrase} (${curr} vs ${prev} across your book).`,
    };
  }
  return {
    tone: 'neutral',
    text: `Referral volume is holding steady ${phrase} (${curr} vs ${prev}, within ±10%).`,
  };
}

function describeAccount(customer, curr, prev, weekRange) {
  const phrase = comparisonPhrase(weekRange);
  const health = customer.health?.color;
  const refC = Number(curr.referrals) || 0;
  const refP = Number(prev.referrals) || 0;
  const totalC = sumMetrics(curr);
  const totalP = sumMetrics(prev);

  if (!weekRange?.hasBusinessDays) {
    return {
      tone: 'neutral',
      text: 'Waiting for the first business day this week — data refreshes at 3 AM.',
    };
  }

  if (totalC === 0 && totalP === 0) {
    return {
      tone: health === 'red' ? 'bad' : 'neutral',
      text: `No activity ${phrase}.`,
    };
  }

  if (totalC === 0 && totalP > 0) {
    return {
      tone: health === 'red' ? 'bad' : 'warn',
      text: `Quiet so far — had ${totalP} event${totalP === 1 ? '' : 's'} ${phrase}.`,
    };
  }

  if (refP > 0 || refC >= 2) {
    const delta = refC - refP;
    const pct = pctChange(refC, refP);
    if (refC === 0 && refP > 0) {
      return {
        tone: health === 'red' ? 'bad' : 'warn',
        text: `Referrals dropped to zero (${refP} ${phrase}).`,
      };
    }
    if (delta >= 2 || pct >= 15) {
      return {
        tone: 'good',
        text: `Referrals up ${fmtDelta(delta)} (${refC} vs ${refP} ${phrase}).`,
      };
    }
    if (delta <= -2 || pct <= -15) {
      return {
        tone: health === 'red' ? 'bad' : 'warn',
        text: `Referrals down ${fmtDelta(delta)} (${refC} vs ${refP} ${phrase}).`,
      };
    }
    return {
      tone: 'neutral',
      text: `Referrals steady (${refC} vs ${refP} ${phrase}).`,
    };
  }

  const pct = pctChange(totalC, totalP);
  if (Math.abs(pct) >= 20 && totalP >= 2) {
    return {
      tone: pct > 0 ? 'good' : (health === 'red' ? 'bad' : 'warn'),
      text: `Activity ${pct > 0 ? 'up' : 'down'} ${fmtPct(pct)} overall ${phrase} (${totalC} vs ${totalP} events).`,
    };
  }

  if (health === 'red') {
    return {
      tone: 'bad',
      text: `Light activity (${totalC} vs ${totalP} events ${phrase}) — health is red.`,
    };
  }
  if (health === 'yellow') {
    return {
      tone: 'warn',
      text: `Light activity (${totalC} vs ${totalP} events ${phrase}) — health is yellow.`,
    };
  }

  return {
    tone: 'neutral',
    text: `Light activity (${totalC} vs ${totalP} events ${phrase}).`,
  };
}

/**
 * @returns {{ summary: Array<{tone,text}>, accounts: Array<{name,tone,text}> }}
 */
export function buildWeeklyHighlights(customers, weeklyMetrics, weekRange) {
  const visible = (customers || []).filter((c) => !c._placeholder);
  if (!visible.length) {
    return { summary: [], accounts: [] };
  }

  const entries = visible.map((customer) => {
    const m = weeklyMetrics?.[String(customer.id)] || {};
    return {
      customer,
      curr: m.current || {},
      prev: m.previous || {},
    };
  });

  const summary = [];
  const phrase = comparisonPhrase(weekRange);

  if (weekRange?.periodNote) {
    summary.push({ tone: 'neutral', text: weekRange.periodNote });
  }

  if (weekRange?.hasBusinessDays) {
    const refTotals = entries.reduce(
      (t, e) => ({
        curr: t.curr + (Number(e.curr.referrals) || 0),
        prev: t.prev + (Number(e.prev.referrals) || 0),
      }),
      { curr: 0, prev: 0 },
    );
    summary.push(describeReferralTrend(refTotals.curr, refTotals.prev, phrase));

    const red = entries.filter((e) => e.customer.health?.color === 'red');
    const yellow = entries.filter((e) => e.customer.health?.color === 'yellow');
    if (red.length > 0) {
      summary.push({
        tone: 'bad',
        text: `${red.length} account${red.length === 1 ? '' : 's'} flagged red on health${red.length <= 3 ? `: ${red.map((e) => e.customer.name).join(', ')}` : ''}.`,
      });
    } else if (yellow.length > 0) {
      summary.push({
        tone: 'warn',
        text: `${yellow.length} account${yellow.length === 1 ? '' : 's'} yellow on health — worth a quick check.`,
      });
    } else {
      const greens = entries.filter((e) => e.customer.health?.color === 'green').length;
      if (greens > 0) {
        summary.push({
          tone: 'good',
          text: `Health looks green across ${greens} of your ${visible.length} account${visible.length === 1 ? '' : 's'}.`,
        });
      }
    }
  }

  const accounts = entries.map(({ customer, curr, prev }) => {
    const bullet = describeAccount(customer, curr, prev, weekRange);
    return {
      name: customer.name,
      tone: bullet.tone,
      text: bullet.text,
    };
  });

  return {
    summary: summary.slice(0, 3),
    accounts,
  };
}
